import React, { useState, useEffect } from 'react';
import Section from '../ui/Section.jsx';
import Avatar from '../ui/Avatar.jsx';
import Skeleton from '../ui/Skeleton.jsx';
import Pagination, { usePagination } from '../ui/Pagination.jsx';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Status colors: calm blue for room, the app's green for on plan, violet for over.
// Deliberately no red — neither room nor overtime is an alarm.
const STATUS = {
  room:    { label: 'Room to track', fg: '#3F58B0', bg: '#EAEEFA' },
  on_plan: { label: 'On plan',       fg: '#1F6B41', bg: '#E8F2EC' },
  over:    { label: 'Over plan',     fg: '#6E4C9E', bg: '#F1EBF8' }
};

const fmt = (n) => Math.round(n || 0).toLocaleString('en-US');

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

function YearLine({ e, since }) {
  const s = STATUS[e.status];
  if (e.status === 'room') {
    return (
      <>
        <div className="text-[13px]" style={{ color: s.fg }}><b className="font-mono">{fmt(-e.difference)} h</b> of room {since ? `since ${since}` : 'this year'}</div>
        <div className="text-[11.5px] mt-0.5" style={{ color: '#6B6B68' }}>track ~{fmt(e.pace)} h/mo to use it by December</div>
      </>
    );
  }
  if (e.status === 'over') {
    return (
      <>
        <div className="text-[13px]" style={{ color: s.fg }}><b className="font-mono">{fmt(e.difference)} h</b> over plan</div>
        <div className="text-[11.5px] mt-0.5" style={{ color: '#6B6B68' }}>track ~{fmt(e.pace)} h/mo, or use {fmt(e.difference)} h for internal work</div>
      </>
    );
  }
  return (
    <>
      <div className="text-[13px]" style={{ color: s.fg }}>On plan</div>
      <div className="text-[11.5px] mt-0.5" style={{ color: '#6B6B68' }}>keep ~{fmt(e.pace)} h/mo</div>
    </>
  );
}

function Label({ children }) {
  return <div className="text-[10.5px] uppercase" style={{ color: '#9A9A95', letterSpacing: '0.06em' }}>{children}</div>;
}

function MonthCell({ value, label, kind }) {
  const base = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '7px 0', borderRadius: 5 };
  const style = kind === 'now'    ? { ...base, background: '#FAFAF7', boxShadow: 'inset 0 0 0 1px #1A1A1A' }
              : kind === 'future' ? { ...base, boxShadow: 'inset 0 0 0 1px #F0EFE9' }
              : kind === 'free'   ? { ...base, boxShadow: 'inset 0 0 0 1px #F0EFE9', background: 'repeating-linear-gradient(135deg, #fff 0 4px, #FAFAF7 4px 8px)' }
              : { ...base, background: '#FAFAF7' };
  return (
    <div style={style}>
      <span className="font-mono text-[12px]" style={{ color: kind === 'future' ? '#6B6B68' : (kind === 'none' || kind === 'free' ? '#9A9A95' : '#1A1A1A') }}>{value}</span>
      <span className="text-[10px]" style={{ color: '#9A9A95' }}>{label}</span>
    </div>
  );
}

function Details({ e, data }) {
  const now = data.current_month;
  const from = data.budget_from || 0;
  const lastClosed = now > 0 ? MONTHS[now - 1] : null;
  const s = STATUS[e.status];
  const total = e.status === 'room' ? ['Room to track', `${fmt(-e.difference)} h`]
              : e.status === 'over' ? ['Over plan', `${fmt(e.difference)} h`]
              : ['On plan', `${e.difference > 0 ? '+' : e.difference < 0 ? '−' : ''}${fmt(Math.abs(e.difference))} h`];
  // The span the plan covers, e.g. "Sep–Nov" (or "Sep" for a single month).
  const span = e.planned_from === null ? null
             : (e.planned_from === now - 1 ? MONTHS[e.planned_from] : `${MONTHS[e.planned_from]}–${lastClosed}`);

  const caption = [
    from > 0 && `${from === 1 ? 'Jan' : `Jan–${MONTHS[from - 1]}`} no limit`,
    now > from && `${from === now - 1 ? MONTHS[from] : `${MONTHS[from]}–${lastClosed}`} tracked`,
    `${MONTHS[now]} so far / plan`,
    now < 11 && `${now === 10 ? 'Dec' : `${MONTHS[now + 1]}–Dec`} plan`
  ].filter(Boolean).join(' · ');

  return (
    <div className="px-5 pb-5" style={{ background: '#FBFBF8' }}>
      <div className="rounded-lg border p-4 grid gap-x-7 gap-y-4 items-start"
           style={{ background: '#fff', borderColor: '#ECEBE5', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <div className="min-w-0">
          <div>
            {/* One row of 12 when wide; wraps (e.g. 2 × 6) in a narrow column. */}
            <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))' }}>
              {MONTHS.map((m, i) => {
                if (i < now) {
                  const h = e.months[i];
                  const kind = i < from ? 'free' : (h === null ? 'none' : 'closed');
                  return <MonthCell key={m} label={m} value={h === null ? '—' : fmt(h)} kind={kind} />;
                }
                if (i === now) {
                  return <MonthCell key={m} label={m} kind="now"
                                    value={<>{fmt(e.current_hours)}<span style={{ color: '#9A9A95' }}>/{fmt(e.pace)}</span></>} />;
                }
                return <MonthCell key={m} label={m} value={fmt(e.pace)} kind="future" />;
              })}
            </div>
          </div>
          <p className="text-[11.5px] mt-2" style={{ color: '#9A9A95' }}>{caption}</p>
        </div>
        <div className="text-[12.5px] flex flex-col gap-1.5">
          {span ? (
            <>
              <Row label={`Budget ${span} (${data.monthly_hours} × ${e.planned_months})`} value={`${fmt(e.planned)} h`} />
              <Row label={`Tracked ${span}`} value={`${fmt(e.tracked)} h`} />
              <Row label={total[0]} value={total[1]} color={s.fg} last />
            </>
          ) : (
            <p style={{ color: '#6B6B68', margin: 0 }}>
              The budget {from === now ? `starts in ${MONTHS_FULL[now]}` : 'has no tracked months yet'}, so there's nothing to catch up. Aim for {fmt(e.pace)} h a month.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color, last }) {
  return (
    <div className="flex justify-between gap-3"
         style={{ borderBottom: last ? 'none' : '1px dashed #F0EFE9', paddingBottom: last ? 0 : 6, fontWeight: last ? 600 : 400, color }}>
      <span>{label}</span><span className="font-mono">{value}</span>
    </div>
  );
}

function NotCounted({ people, budget, month }) {
  const [open, setOpen] = useState(false);
  if (!people.length) return null;
  return (
    <div className="border-t" style={{ borderColor: '#F0EFE9' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full text-left px-5 py-3 text-[12px] hover:bg-[#FBFBF8]"
        style={{ color: '#6B6B68', background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <span style={{ display: 'inline-block', width: 12, color: '#9A9A95', transition: 'transform 120ms',
                       transform: open ? 'rotate(90deg)' : 'none' }}>›</span>
        {people.length} not counted · average under {fmt(budget / 2)} h/month on this project
      </button>
      {open && (
        <ul className="px-5 pb-3">
          {people.map((p) => (
            <li key={p.id} className="flex items-center gap-2.5 py-1.5">
              <Avatar initials={p.initials} seed={p.name} size={22} />
              <span className="flex-1 text-[12.5px]" style={{ color: '#3A3A38' }}>{p.name}</span>
              <span className="font-mono text-[11.5px]" style={{ color: '#6B6B68' }}>
                {fmt(p.average_hours)} h/mo avg · {fmt(p.current_hours)} h in {month}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const btnStyle = { borderColor: '#E0DFD9', background: '#fff', color: '#1A1A1A', cursor: 'pointer' };

// Set up / change the project's limit. Nothing is saved until "Save".
function LimitForm({ data, saving, onSave, onCancel, onRemove }) {
  // The form works without a report (limit off), so take "now" from the browser.
  const today = new Date();
  const currentMonth = data.enabled ? data.current_month : today.getMonth();
  const year = data.enabled ? data.year : today.getFullYear();
  const [hours, setHours] = useState(String(data.enabled ? data.monthly_hours : data.default_monthly_hours));
  const [from, setFrom] = useState(data.enabled ? (data.budget_from || 0) : currentMonth);

  function submit(ev) {
    ev.preventDefault();
    onSave({
      monthly_hours: parseInt(hours, 10) || '',
      starts_on:     from === 0 ? '' : `${year}-${String(from + 1).padStart(2, '0')}`
    });
  }

  const field = { padding: '5px 8px', borderColor: '#E0DFD9', background: '#fff' };
  return (
    <form onSubmit={submit} className="rounded-lg border p-4 mb-3 flex flex-wrap items-end gap-x-5 gap-y-3"
          style={{ background: '#FBFBF8', borderColor: '#ECEBE5' }}>
      <label className="flex flex-col gap-1 text-[11px] uppercase" style={{ color: '#9A9A95', letterSpacing: '0.06em' }}>
        Hours per engineer
        <span className="flex items-center gap-1.5 normal-case text-[12px]" style={{ color: '#6B6B68', letterSpacing: 0 }}>
          <input type="number" min="1" max="300" required value={hours} onChange={(ev) => setHours(ev.target.value)}
                 aria-label="Hours per engineer per month" className="font-mono text-[13px] rounded-md border text-right"
                 style={{ ...field, width: 72 }} />
          h / month
        </span>
      </label>
      <label className="flex flex-col gap-1 text-[11px] uppercase" style={{ color: '#9A9A95', letterSpacing: '0.06em' }}>
        Starts from
        <select value={from} onChange={(ev) => setFrom(parseInt(ev.target.value, 10))} aria-label="Limit starts from"
                className="text-[13px] rounded-md border normal-case" style={{ ...field, letterSpacing: 0, color: '#1A1A1A' }}>
          {MONTHS_FULL.slice(0, currentMonth + 1).map((m, i) => <option key={m} value={i}>{m} {year}</option>)}
        </select>
      </label>
      <span className="flex items-center gap-2">
        <button type="submit" disabled={saving} className="text-[12px] px-3 py-1.5 rounded-md border"
                style={{ background: '#1A1A1A', borderColor: '#1A1A1A', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className="text-[12px] px-3 py-1.5 rounded-md border" style={btnStyle}>
          Cancel
        </button>
      </span>
      {data.enabled && (
        <button type="button" onClick={onRemove} disabled={saving} className="text-[12px] ml-auto"
                style={{ background: 'none', border: 'none', color: '#6B6B68', textDecoration: 'underline', cursor: 'pointer' }}>
          Remove limit
        </button>
      )}
      <p className="w-full text-[11.5px] m-0" style={{ color: '#9A9A95' }}>
        Months before the start have no limit. Each engineer's plan counts from the start month.
      </p>
    </form>
  );
}

function LimitSummary({ data, onEdit }) {
  const from = data.budget_from || 0;
  return (
    <span className="flex items-center gap-2 text-[12px] whitespace-nowrap" style={{ color: '#6B6B68' }}>
      <span title="Hours per engineer per month">
        <b className="font-mono" style={{ color: '#1A1A1A', fontWeight: 500 }}>{data.monthly_hours}</b> h/mo
        {from > 0 && <> from <b style={{ color: '#1A1A1A', fontWeight: 500 }}>{MONTHS[from]}</b></>}
      </span>
      {data.can_edit && (
        <button type="button" onClick={onEdit} className="text-[12px] px-2.5 py-1 rounded-md border hover:bg-[#FBFBF8]" style={btnStyle}>
          Edit
        </button>
      )}
    </span>
  );
}

export default function HoursBudget({ project, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(false);
  // Hooks run on every render, so paginate before the loading/error returns.
  const { pageItems, ...pager } = usePagination(data?.engineers || [], 20);

  // Loads on mount; refreshKey > 0 means the page's "Sync with Redmine" was
  // pressed, so bypass the server cache like the Redmine block does.
  useEffect(() => {
    setError(false);
    setData(null);
    fetch(`/projects/${project.id}/hours_budget${refreshKey > 0 ? '?refresh=1' : ''}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((res) => {
        if (res.status === 401) { window.location = '/session/new'; return null; }
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then((json) => { if (json) setData(json); })
      .catch(() => setError(true));
  }, [project.id, refreshKey]);

  // PATCH saves (or creates) the limit, DELETE removes it; both answer with the
  // block's new state.
  function send(method, body) {
    setSaving(true);
    setSaveError(null);
    fetch(`/projects/${project.id}/hours_budget`, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      body: body ? JSON.stringify(body) : undefined
    })
      .then(async (res) => {
        if (res.status === 401) { window.location = '/session/new'; return; }
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error((json.errors || []).join(', ') || `Couldn't save (${res.status})`);
        setData(json);
        setEditing(false);
      })
      .catch((err) => setSaveError(err.message))
      .finally(() => setSaving(false));
  }

  const form = editing && (
    <LimitForm
      data={data}
      saving={saving}
      onSave={(attrs) => send('PATCH', { hours_budget: attrs })}
      onCancel={() => { setEditing(false); setSaveError(null); }}
      onRemove={() => { if (window.confirm('Remove the hours limit from this project?')) send('DELETE'); }}
    />
  );
  const errorLine = saveError && <div className="text-[12px] mb-2" style={{ color: '#9B1F26' }}>{saveError}</div>;

  const title = 'Hours budget';

  if (error) {
    return (
      <Section id="hours-budget" title={title} count="—">
        <div className="text-[12px]" style={{ color: '#9A9A95' }}>Live hours data unavailable.</div>
      </Section>
    );
  }

  if (!data) {
    return (
      <Section id="hours-budget" title={title} count="Loading…">
        <div className="rounded-lg border" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-6 px-5 py-4 border-b last:border-b-0" style={{ borderColor: '#F0EFE9' }}>
              <Skeleton width={28} height={28} radius={999} />
              <span className="flex-1"><Skeleton width="30%" height={13} /></span>
              <span className="flex-1"><Skeleton width="60%" height={13} /></span>
              <Skeleton width={80} height={18} radius={999} />
            </div>
          ))}
        </div>
      </Section>
    );
  }

  if (!data.enabled) {
    return (
      <Section id="hours-budget" title={title} count="Off">
        {errorLine}
        {form || (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-[12px]" style={{ color: '#9A9A95' }}>
              No monthly hours limit on this project.
            </span>
            {data.can_edit && (
              <button type="button" onClick={() => setEditing(true)}
                      className="text-[12px] px-2.5 py-1 rounded-md border hover:bg-[#FBFBF8]" style={btnStyle}>
                Set hours limit
              </button>
            )}
          </div>
        )}
      </Section>
    );
  }

  const now = data.current_month;
  const engineers = pageItems;

  return (
    <Section
      id="hours-budget"
      title={title}
      count={String(data.year)}
      headerRight={editing ? null : <LimitSummary data={data} onEdit={() => setEditing(true)} />}
    >
      {errorLine}
      {form}
      <div className="rounded-lg border" style={{ background: '#fff', borderColor: '#ECEBE5' }}>
        {data.engineers.length === 0 ? (
          <div className="px-5 py-4 text-[12px]" style={{ color: '#9A9A95' }}>No one is tracking time on this project yet.</div>
        ) : engineers.map((e) => {
          const s = STATUS[e.status];
          const isOpen = open === e.id;
          const pct = e.pace > 0 ? Math.min(100, (e.current_hours / e.pace) * 100) : 100;
          return (
            <div key={e.id} className="border-b last:border-b-0" style={{ borderColor: '#F0EFE9' }}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : e.id)}
                aria-expanded={isOpen}
                className="w-full block text-left px-5 py-3.5 hover:bg-[#FBFBF8]"
                style={{ background: isOpen ? '#FBFBF8' : 'transparent', border: 'none', cursor: 'pointer' }}
              >
                <span className="flex items-center gap-2.5">
                  <Avatar initials={e.initials} seed={e.name} />
                  <span className="flex-1 min-w-0 text-[13px] font-medium truncate" style={{ color: '#1A1A1A' }}>{e.name}</span>
                  <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{ background: s.bg, color: s.fg, fontWeight: 500 }}>{s.label}</span>
                </span>
                {/* Two columns when there's room, stacked in a narrow main column. */}
                <span className="grid mt-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', columnGap: 24, rowGap: 12, paddingLeft: 40 }}>
                  <span>
                    <Label>{MONTHS_FULL[now]}</Label>
                    <div className="font-mono text-[14.5px] font-medium" style={{ color: '#1A1A1A' }}>
                      {fmt(e.current_hours)} <span style={{ color: '#9A9A95', fontWeight: 400 }}>of {fmt(e.pace)} h</span>
                    </div>
                    <div className="mt-1.5 rounded-full overflow-hidden" style={{ height: 6, background: '#F0EFE9' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#3A3A38' }} />
                    </div>
                    <div className="text-[11.5px] mt-1" style={{ color: '#6B6B68' }}>{fmt(Math.max(0, e.pace - e.current_hours))} h left this month</div>
                  </span>
                  <span>
                    <Label>{data.budget_from > 0 ? `Since ${MONTHS[data.budget_from]}` : 'This year'}</Label>
                    <YearLine e={e} since={data.budget_from > 0 ? MONTHS[data.budget_from] : null} />
                  </span>
                </span>
              </button>
              {isOpen && <Details e={e} data={data} />}
            </div>
          );
        })}
        <NotCounted people={data.not_counted || []} budget={data.monthly_hours} month={MONTHS[now]} />
      </div>
      <Pagination {...pager} />
      <div className="mt-2 text-[11.5px]" style={{ color: '#9A9A95' }}>
        Pulled from Redmine · the year counts from each engineer's first month with tracked time on this project.
      </div>
    </Section>
  );
}
